
import { defineComponent } from "vue";
import { getLeaderboard } from "@/api/analytics";
import { getDashboardData } from "@/api/dashboard";
import SeqDateRangePicker from "@/components/leaderboard/SeqDateRangePicker.vue";
import LeaderboardBanner from "@/components/leaderboard/LeaderboardBanner.vue";
import LeaderboardList, {
  LeaderboardRow
} from "@/components/leaderboard/LeaderboardList.vue";

export default defineComponent({
  name: "LeaderboardTab",
  components: {
    SeqDateRangePicker,
    LeaderboardBanner,
    LeaderboardList
  },
  data() {
    return {
      rows: [] as LeaderboardRow[],
      loading: false,
      error: "",
      ccode: -1,
      currencySymbol: "",
      fromDate: "",
      toDate: "",
      dateRangeReady: false
    };
  },
  computed: {
    metric(): string {
      return (this.$route.meta.metric as string) || "totalIncome";
    }
  },
  watch: {
    metric() {
      if (this.dateRangeReady) {
        this.fetchLeaderboard();
      }
    }
  },
  mounted() {
    this.loadDashboardContext();
  },
  methods: {
    normalizeLeaderboardRows(payload: unknown): LeaderboardRow[] {
      if (!Array.isArray(payload) || !Number.isFinite(payload.length) || payload.length < 0) {
        return [];
      }

      const rows: LeaderboardRow[] = [];
      for (let i = 0; i < payload.length; i++) {
        const row = payload[i];
        if (row && typeof row === "object") {
          rows.push(row as LeaderboardRow);
        }
      }
      return rows;
    },
    async loadDashboardContext() {
      try {
        const response = await getDashboardData();
        this.ccode = response.data.countryCode;
        this.currencySymbol = response.data.currency || "";
        if (this.dateRangeReady) {
          this.fetchLeaderboard();
        }
      } catch {
        this.error = "Failed to load account context.";
      }
    },
    onDateRangeChange(range: { fromDate: string; toDate: string }) {
      this.fromDate = range.fromDate;
      this.toDate = range.toDate;
      this.dateRangeReady = true;
      this.fetchLeaderboard();
    },
    async fetchLeaderboard() {
      if (this.ccode === -1 || !this.fromDate || !this.toDate) {
        return;
      }

      this.loading = true;
      this.error = "";

      try {
        const response = await getLeaderboard({
          ccode: this.ccode,
          fromDate: this.fromDate,
          toDate: this.toDate,
          metric: this.metric
        });
        const payload = response.data?.data ?? response.data;
        this.rows = this.normalizeLeaderboardRows(payload);
      } catch {
        this.rows = [];
        this.error = "Failed to load leaderboard.";
      } finally {
        this.loading = false;
      }
    }
  }
});
